import { useCallback, useEffect, useState } from "react";
import {
  Plus, Pencil, Trash2, Globe, Router, Wifi, RefreshCw, AlertTriangle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const TYPE_LABELS = { router: "Router", website: "Sitio web", internet: "Conexión a internet" };
const TYPE_ICONS = { router: Router, website: Globe, internet: Wifi };

const EMPTY = { name: "", type: "router", target: "", custom_port: "", active: true };

const toPayload = (f) => ({
  ...f,
  custom_port: f.custom_port ? parseInt(f.custom_port, 10) : null,
});

function StatusBadge({ status, testId }) {
  if (status === "online") {
    return (
      <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> En línea
      </span>
    );
  }
  if (status === "offline") {
    return (
      <span data-testid={testId} className="animate-pulse-soft inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Fuera de línea
      </span>
    );
  }
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-500">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Sin verificar
    </span>
  );
}

function DeviceForm({ form, setForm, prefix }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Nombre</Label>
        <Input
          required
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Ej: Router Oficina Central"
          data-testid={`${prefix}-name-input`}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Tipo</Label>
        <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
          <SelectTrigger data-testid={`${prefix}-type-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="router">Router</SelectItem>
            <SelectItem value="website">Sitio web</SelectItem>
            <SelectItem value="internet">Conexión a internet</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Dirección IP pública o URL</Label>
        <Input
          required
          value={form.target}
          onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
          placeholder={form.type === "website" ? "https://ejemplo.com" : "Ej: 190.12.34.56"}
          data-testid={`${prefix}-target-input`}
        />
      </div>
      {form.type !== "website" && (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Puerto personalizado (opcional)</Label>
          <Input
            type="number" min="1" max="65535"
            value={form.custom_port}
            onChange={(e) => setForm((f) => ({ ...f, custom_port: e.target.value }))}
            placeholder="Por defecto: 80, 443, 22"
            data-testid={`${prefix}-port-input`}
          />
          <p className="text-xs text-slate-400">Si se indica, la verificación TCP usará únicamente este puerto.</p>
        </div>
      )}
    </div>
  );
}

export default function Monitor() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [devices, setDevices] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/monitor/devices");
      setDevices(data);
    } catch (e) {
      toast.error(apiError(e, "No se pudo cargar el monitoreo"));
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/monitor/devices", toPayload(form));
      toast.success(`Dispositivo "${form.name}" registrado`);
      setCreateOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo registrar el dispositivo"));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (d) => {
    setForm({ name: d.name, type: d.type, target: d.target, custom_port: d.custom_port ?? "", active: d.active });
    setEditTarget(d);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/monitor/devices/${editTarget.id}`, toPayload(form));
      toast.success("Dispositivo actualizado");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo actualizar el dispositivo"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/monitor/devices/${deleteTarget.id}`);
      toast.success(`Dispositivo "${deleteTarget.name}" eliminado`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo eliminar el dispositivo"));
    } finally {
      setSaving(false);
    }
  };

  const checkNow = async (d) => {
    setChecking(d.id);
    try {
      await api.post(`/monitor/devices/${d.id}/check`);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo verificar el dispositivo"));
    } finally {
      setChecking("");
    }
  };

  const offline = (devices || []).filter((d) => d.last_status === "offline" && d.active);
  const onlineCount = (devices || []).filter((d) => d.last_status === "online").length;

  return (
    <div className="space-y-4" data-testid="monitor-page">
      {offline.length > 0 && (
        <div
          className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4"
          data-testid="monitor-alert-banner"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div>
            <p className="font-heading text-sm font-bold text-red-800">
              {offline.length === 1
                ? "1 dispositivo fuera de línea"
                : `${offline.length} dispositivos fuera de línea`}
            </p>
            <p className="mt-0.5 text-sm text-red-700">
              {offline.map((d) => d.name).join(", ")} — se ha notificado por correo a soporte.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500" data-testid="monitor-summary">
          {devices
            ? `${devices.length} dispositivo${devices.length === 1 ? "" : "s"} · ${onlineCount} en línea · verificación automática cada 2 min`
            : "Cargando dispositivos..."}
        </p>
        {isAdmin && (
          <Button
            onClick={() => { setForm(EMPTY); setCreateOpen(true); }}
            className="gap-2 bg-primary hover:bg-[#0353E9]"
            data-testid="new-device-button"
          >
            <Plus className="h-4 w-4" /> Nuevo Dispositivo
          </Button>
        )}
      </div>

      {devices === null ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-36 w-full rounded-lg" />)}
        </div>
      ) : devices.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="monitor-empty">
          <p className="font-heading text-lg font-semibold text-slate-700">No hay dispositivos registrados</p>
          <p className="mt-1 text-sm text-slate-500">
            {isAdmin ? "Registre routers, sitios web o conexiones para monitorear su disponibilidad." : "El administrador puede registrar dispositivos a monitorear."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {devices.map((d) => {
            const Icon = TYPE_ICONS[d.type] || Globe;
            return (
              <div
                key={d.id}
                data-testid={`device-card-${d.id}`}
                className={`rounded-lg border bg-white p-5 transition-shadow duration-200 hover:shadow-sm ${
                  d.last_status === "offline" ? "border-red-300" : "border-slate-200"
                } ${!d.active ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`rounded-md p-2 ${d.last_status === "offline" ? "bg-red-50 text-red-600" : "bg-blue-50 text-primary"}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-heading text-sm font-bold text-slate-900">{d.name}</p>
                      <p className="text-xs text-slate-500">{TYPE_LABELS[d.type] || d.type}{!d.active ? " · inactivo" : ""}</p>
                    </div>
                  </div>
                  <StatusBadge status={d.last_status} testId={`device-status-${d.id}`} />
                </div>
                <p className="mt-3 truncate font-mono text-xs text-slate-600" data-testid={`device-target-${d.id}`}>{d.target}</p>
                <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                  <div className="text-xs text-slate-500">
                    <span data-testid={`device-latency-${d.id}`} className="font-mono font-semibold text-slate-700">
                      {d.last_latency_ms != null ? `${d.last_latency_ms} ms` : "—"}
                    </span>
                    {d.last_port != null && (
                      <span data-testid={`device-port-${d.id}`} className="ml-1.5 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 font-mono text-xs font-medium text-blue-700">
                        Puerto {d.last_port}
                      </span>
                    )}
                    {" · "}
                    {d.last_checked_at ? `verificado ${String(d.last_checked_at).slice(11, 19)} UTC` : "sin verificar"}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-primary"
                        onClick={() => checkNow(d)} disabled={checking === d.id}
                        data-testid={`device-check-${d.id}`}
                      >
                        {checking === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-primary"
                        onClick={() => openEdit(d)}
                        data-testid={`device-edit-${d.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600"
                        onClick={() => setDeleteTarget(d)}
                        data-testid={`device-delete-${d.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Nuevo Dispositivo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5" data-testid="device-form">
            <DeviceForm form={form} setForm={setForm} prefix="device" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-primary hover:bg-[#0353E9]" data-testid="device-form-submit">
                {saving ? "Guardando..." : "Registrar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onOpenChange={() => setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Dispositivo — {editTarget?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-5" data-testid="device-edit-form">
            <DeviceForm form={form} setForm={setForm} prefix="device-edit" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-primary hover:bg-[#0353E9]" data-testid="device-edit-submit">
                {saving ? "Guardando..." : "Guardar cambios"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">¿Eliminar dispositivo {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se detendrá el monitoreo de este dispositivo. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-device-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-device-confirm"
            >
              {saving ? "Eliminando..." : "Eliminar dispositivo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
